import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import useOpeningHoursQuery from '../api/queries/useOpeningHoursQuery';

function Footer() {
    const { t, i18n } = useTranslation('global', { keyPrefix: 'footer' });
    const { t: url } = useTranslation('urls');
    const { isLoading, data } = useOpeningHoursQuery();
    const day = useMemo<keyof Hours>(() => {
        const days = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];
        return days[new Date().getDay()] as keyof Hours;
    }, []);

    return (
        <div className="min-h-[320px] bg-black px-[20px] py-[48px] text-2xl font-medium text-white lg:px-[60px] lg:pt-[60px]">
            <div className="flex flex-col lg:flex-row lg:justify-between">
                <div className="w-[320px]">
                    <svg
                        className="inline"
                        width="36"
                        height="32"
                        viewBox="0 0 36 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M14.776 0.127998C12.856 0.127998 11.416 1.312 8.856 3.744L8.952 3.84C9.784 3.232 10.68 2.848 12.248 2.848C14.968 2.848 16.792 4.096 16.792 7.648V24H20.504V6.848C20.504 2.176 18.136 0.127998 14.776 0.127998ZM35.704 20.992L35.64 20.928C35 21.408 34.552 21.632 33.752 21.632C32.12 21.632 30.136 19.904 28.728 19.2L29.752 18.176C31.896 16.032 32.6 15.072 32.6 11.968V7.04C32.6 2.176 30.2 0.127998 26.712 0.127998C24.728 0.127998 23.544 1.024 20.696 3.904L20.792 4C21.784 3.2 22.776 2.848 24.408 2.848C27 2.848 28.888 4.128 28.888 7.648V15.456C28.888 18.304 28.44 18.848 27 20.224L25.624 21.536V21.664C27.064 22.4 29.208 24.512 31.032 24.512C32.408 24.512 33.176 23.744 35.704 20.992ZM4.632 4.032H7.576V4.128C5.336 6.112 4.568 7.264 4.568 9.504V16.192C4.568 18.528 4.408 19.2 3.576 20.192L3.672 20.256L5.016 19.168C7.736 16.96 8.28 15.84 8.28 13.184V4.256C8.28 2.688 8.536 1.696 9.144 0.639999H6.52C4.792 0.639999 3.544 1.152 2.008 2.944L0.28 4.928L0.376 5.024C1.304 4.192 2.008 4.032 4.632 4.032ZM10.712 21.376C8.856 21.376 7.16 20.192 5.24 20.192C3.672 20.192 2.616 21.376 0.408 23.968L0.472 24.032C1.112 23.52 1.56 23.296 2.52 23.296C4.408 23.296 6.136 24.512 7.8 24.512C9.4 24.512 10.136 23.488 12.6 20.768L12.536 20.704C11.96 21.152 11.48 21.376 10.712 21.376Z"
                            fill="white"
                        />
                    </svg>
                    UO
                    <span className="ml-2 text-dark">{t('title')}</span>
                </div>

                <div className="py-[32px] lg:py-0 xl:text-right">
                    {!isLoading &&
                        data &&
                        data.branches
                            .filter(
                                (branch) =>
                                    branch.lang === i18n.resolvedLanguage,
                            )
                            .map((branch) => {
                                const hours = branch.openHours.custom.length
                                    ? branch.openHours.custom
                                    : branch.openHours[day];
                                return (
                                    <div key={`branch-${branch.name}`}>
                                        <div className="xl:inline">
                                            {branch.name}
                                        </div>
                                        <a
                                            href={branch.moreLink}
                                            rel="noopener noreferrer"
                                            className="min-w-[240px] text-dark xl:pl-4">
                                            {hours}&nbsp;→
                                        </a>
                                    </div>
                                );
                            })}
                </div>
            </div>
            <div className="mt-[30px] flex flex-col text-base lg:flex-row">
                <div>
                    <a
                        href={`https://muo.cz/${url('contact')}`}
                        rel="noopener noreferrer"
                        className="order-0 mr-[30px] hidden border-b-2 border-b-white py-3 tracking-[4px] lg:block">
                        →&nbsp;{t('contact')}
                    </a>
                </div>

                <div className="order-2 flex grow flex-col flex-wrap lg:order-1 lg:flex-row lg:justify-end">
                    <div className="mb-[20px] mr-[30px]">
                        <a
                            href="https://www.facebook.com/muzeumumeni"
                            rel="noopener noreferrer"
                            className="inline-block border-b-2 border-b-white py-3 tracking-[4px] hover:border-b-dark">
                            →&nbsp;{t('facebook')}
                        </a>
                    </div>
                    <div className="mb-[20px] mr-[30px]">
                        <a
                            href="https://twitter.com/muzeumumeni"
                            rel="noopener noreferrer"
                            className="inline-block border-b-2 border-b-white py-3 tracking-[4px] hover:border-b-dark">
                            →&nbsp;{t('twitter')}
                        </a>
                    </div>
                    <div className="mb-[20px] mr-[30px]">
                        <a
                            href="https://www.instagram.com/muzeumumeni/"
                            rel="noopener noreferrer"
                            className="inline-block border-b-2 border-b-white py-3 tracking-[4px] hover:border-b-dark">
                            →&nbsp;{t('instagram')}
                        </a>
                    </div>
                    <div className="mb-[20px]">
                        <a
                            href="https://www.youtube.com/channel/UCsOguPgXEJoNMYPUNiS669A"
                            rel="noopener noreferrer"
                            className="inline-block border-b-2 border-b-white py-3 tracking-[4px] hover:border-b-dark">
                            →&nbsp;{t('youtube')}
                        </a>
                    </div>
                </div>

                <div className="order-1 mb-[20px] min-w-[240px] lg:order-2 lg:text-right">
                    <span className="relative">
                        <a
                            className="inline-block cursor-pointer border-b-2 border-b-white bg-black py-3 pr-8 uppercase tracking-[4px] text-white outline-0"
                            href={
                                i18n.resolvedLanguage === 'cs'
                                    ? `/en/eshop`
                                    : `/eshop`
                            }>
                            {i18n.resolvedLanguage === 'cs'
                                ? 'English'
                                : 'Čeština'}
                            <span className="absolute right-0 top-[-3px] bg-black px-2 text-white">
                                ↓
                            </span>
                        </a>
                    </span>
                </div>
            </div>

            <div className="mt-[18px] flex flex-col justify-between border-t-2 border-t-white pt-[6px] text-base lg:flex-row lg:pt-[32px]">
                <div className="lg:order-0 order-1 my-[32px] pt-[6px] lg:my-0">
                    {t('copyright')}
                </div>
                <div className="order-0 flex flex-col whitespace-nowrap lg:order-1 lg:flex-row lg:flex-wrap">
                    <Link className="my-[6px] mr-4" to={`/${url('terms')}`}>
                        {t('terms')}
                    </Link>
                    <Link
                        className="my-[6px] mr-4"
                        to={`https://muo.cz/${url('cookies')}`}>
                        {t('cookies')}
                    </Link>
                    <Link
                        className="my-[6px] mr-4"
                        to={`https://muo.cz/${url('privacy')}`}>
                        {t('privacy')}
                    </Link>
                    <a
                        className="my-[6px] mr-4"
                        href="/eshop/img/cms/odstoupeni_od_smlouvy.pdf"
                        target="_blank">
                        {t('withdrawal')}
                    </a>
                    <Link className="my-[6px]" to={`/${url('download')}`}>
                        {t('download')}
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default Footer;
